import clsx from 'clsx';
import { omit } from 'lodash';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { useLLMAgent } from '@/hooks/llm';
import { subscribeAgentRunEvents } from '@/hooks/llm/use-llm-agent';

import { AiButton } from '@flintmint/web-shared/components/ai-button';

import { getActiveBlockId } from '@/utils/editor-utils';

import { getTextGenerationKey, textGenerationMapAtom } from '@/state';

import type { RxDocument } from 'rxdb';
import type { SlashMenuProps } from '@/extensions/slash-command';
import { Block, Note, UserSource } from '@flintmint/core-shared/rxdb';

import styles from './generate-text.module.scss';

export const GenerateText = (props: GenerateTextProps) => {
  const { includeContext, autoFocus, range, editor, editorType, llmContext, close } = props;

  const [textGenerationMap, setTextGenerationMap] = useAtom(textGenerationMapAtom);

  const textGenerationKey = getTextGenerationKey({
    documentId: editor.storage.documentInfo.documentId,
    editorType,
  });

  const [value, setValue] = useState(textGenerationMap[textGenerationKey]?.prompt ?? '');

  const inputRef = useRef<HTMLInputElement | null>(null);

  const persistDocumentPatch = editor.storage.documentInfo.persistDocumentPatch;
  const document = llmContext?.note ?? llmContext?.block ?? llmContext?.userSource;

  const { generateText, isActive: isLoading } = useLLMAgent({
    requestId: textGenerationKey,
    editor,
    document,
    persistDocumentPatch,
  });

  useEffect(() => {
    autoFocus && inputRef.current?.focus();
  }, [autoFocus]);

  const onGenerateText = () => {
    if (value.trim()) {
      const capturedRange = range;
      const capturedActiveBlockId = getActiveBlockId(editor);

      setTextGenerationMap((prev) => ({
        ...prev,
        [textGenerationKey]: {
          focusPos: range.to,
          prompt: value,
          range,
        },
      }));

      let completed = false;
      const unsubscribe = subscribeAgentRunEvents(({ requestId, event }) => {
        if (requestId === textGenerationKey && event.type === 'completed') completed = true;
      });

      generateText({
        userPrompt: value,
        includeContext,
        activeBlockId: capturedActiveBlockId,
        editor,
        position: capturedRange,
        persistDocumentPatch,
        ...llmContext,
      }).finally(() => {
        unsubscribe();
        setTextGenerationMap((prev) => omit(prev, textGenerationKey));
        if (!inputRef.current) return;
        // A completed run leaves its text for review, so the menu closes. Any other end (a refusal, an error, a
        // cancel) keeps the prompt open, and focus returns to it so Enter resends it: submitting focused the editor.
        if (completed) close();
        else focusOnceInteractive(inputRef.current);
      });
    }
  };

  return (
    <label className={clsx(styles.generateText, { loading: isLoading })}>
      <input
        type="text"
        value={value}
        ref={inputRef}
        readOnly={isLoading}
        placeholder="Type any topic for AI"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onGenerateText()}
      />
      <AiButton size="medium" icon="arrow-up-02" loading={isLoading} onClick={onGenerateText} disabled={!value.trim()} />
    </label>
  );
};

/**
 * Focuses `element` now, or, while a modal layer holds it inert (a refusal's invitation opens before the run's
 * promise settles), once that layer lets go: the layer hands focus back to its opener as it closes, so this runs
 * after that and wins.
 */
const focusOnceInteractive = (element: HTMLElement) => {
  const inertAncestor = element.closest('[inert]');
  if (!inertAncestor) {
    element.focus();
    return;
  }

  const observer = new MutationObserver(() => {
    if (inertAncestor.hasAttribute('inert')) return;
    observer.disconnect();
    element.focus();
  });
  observer.observe(inertAncestor, { attributes: true, attributeFilter: ['inert'] });
};

export interface GenerateTextProps extends SlashMenuProps {
  autoFocus?: boolean;
  includeContext?: boolean;

  llmContext?: { note?: RxDocument<Note>; block?: RxDocument<Block>; userSource?: RxDocument<UserSource> };
}
